/**
 * Permissoes em bitfield, no mesmo estilo do Discord.
 * Usamos BigInt para nao esbarrar no limite de 32 bits do operador `|` do JS.
 */
export const Permission = {
  /** Ver o canal e ler o historico */
  VIEW_CHANNEL: 1n << 0n,
  /** Enviar mensagens de texto */
  SEND_MESSAGES: 1n << 1n,
  /** Apagar mensagens dos outros */
  MANAGE_MESSAGES: 1n << 2n,
  /** Entrar em canais de voz */
  CONNECT: 1n << 3n,
  /** Transmitir o proprio microfone */
  SPEAK: 1n << 4n,
  /** Compartilhar a tela */
  SHARE_SCREEN: 1n << 5n,
  /** Ligar a camera */
  VIDEO: 1n << 6n,
  /** Silenciar/ensurdecer outros membros */
  MUTE_MEMBERS: 1n << 7n,
  /** Mover ou desconectar membros de canais de voz */
  MOVE_MEMBERS: 1n << 8n,
  /** Remover membros da guild */
  KICK_MEMBERS: 1n << 9n,
  /** Criar, editar e apagar canais */
  MANAGE_CHANNELS: 1n << 10n,
  /** Criar, editar e apagar cargos */
  MANAGE_ROLES: 1n << 11n,
  /** Gerar codigos de convite */
  CREATE_INVITE: 1n << 12n,
  /** Editar nome/icone da guild */
  MANAGE_GUILD: 1n << 13n,
  /** Ignora todas as checagens acima */
  ADMINISTRATOR: 1n << 14n,
} as const;

export type PermissionName = keyof typeof Permission;

export const PERMISSION_LABELS: Record<PermissionName, string> = {
  VIEW_CHANNEL: 'Ver canal',
  SEND_MESSAGES: 'Enviar mensagens',
  MANAGE_MESSAGES: 'Gerenciar mensagens',
  CONNECT: 'Entrar em voz',
  SPEAK: 'Falar',
  SHARE_SCREEN: 'Compartilhar tela',
  VIDEO: 'Usar camera',
  MUTE_MEMBERS: 'Silenciar membros',
  MOVE_MEMBERS: 'Mover membros',
  KICK_MEMBERS: 'Expulsar membros',
  MANAGE_CHANNELS: 'Gerenciar canais',
  MANAGE_ROLES: 'Gerenciar cargos',
  CREATE_INVITE: 'Criar convites',
  MANAGE_GUILD: 'Gerenciar guild',
  ADMINISTRATOR: 'Administrador',
};

/** Permissoes que um membro comum recebe por padrao (cargo @everyone). */
export const DEFAULT_PERMISSIONS =
  Permission.VIEW_CHANNEL |
  Permission.SEND_MESSAGES |
  Permission.CONNECT |
  Permission.SPEAK |
  Permission.SHARE_SCREEN |
  Permission.VIDEO;

/** Tudo ligado. Usado pelo cargo do dono da guild. */
export const ALL_PERMISSIONS = Object.values(Permission).reduce((acc, p) => acc | p, 0n);

export function has(permissions: bigint, needed: bigint): boolean {
  if ((permissions & Permission.ADMINISTRATOR) === Permission.ADMINISTRATOR) return true;
  return (permissions & needed) === needed;
}

export function listPermissions(permissions: bigint): PermissionName[] {
  return (Object.keys(Permission) as PermissionName[]).filter(
    (name) => (permissions & Permission[name]) === Permission[name],
  );
}

/** Serializacao: bigint nao sobrevive a JSON, entao viaja como string. */
export function encodePermissions(permissions: bigint): string {
  return permissions.toString();
}

export function decodePermissions(value: string | number | bigint | null | undefined): bigint {
  if (value === null || value === undefined || value === '') return 0n;
  try {
    return BigInt(value);
  } catch {
    return 0n;
  }
}

/**
 * Aplica as sobrescritas de um canal sobre as permissoes base do membro.
 * Regra: nega primeiro, depois permite (permitir vence a negacao do mesmo nivel).
 */
export function applyOverwrites(
  base: bigint,
  overwrites: Array<{ allow: bigint; deny: bigint }>,
): bigint {
  if ((base & Permission.ADMINISTRATOR) === Permission.ADMINISTRATOR) return ALL_PERMISSIONS;
  let deny = 0n;
  let allow = 0n;
  for (const ow of overwrites) {
    deny |= ow.deny;
    allow |= ow.allow;
  }
  return (base & ~deny) | allow;
}
